import { type FC, type ReactNode } from "react";
import { type ActiveStorageBlob } from "react-activestorage";

import { faArrowUp } from "@fortawesome/pro-duotone-svg-icons/faArrowUp";
import { faFileLines } from "@fortawesome/pro-duotone-svg-icons/faFileLines";
import { faXmark } from "@fortawesome/pro-duotone-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { EvaluationTypeEnum } from "@app_schema";

import { Button } from "@styled/button";
import { FileManager } from "@styled/file_manager";
import { FileQueue } from "@styled/file_queue";

import type { AddRecordsChoice } from "./add_records_classify_overlay";
import { ClassifyOverlayShell } from "./classify_overlay_shell";
import { EvalTypeBadge } from "./eval_type_badge";
import { PriorDrop } from "./prior_drop";
import {
  RecordsStatusBar,
  RsbActionButton,
  RsbCheckIcon,
} from "./records_status_bar";
import { renderFileQueueItem } from "./shared_card_utils";
import { SourcePdfBanner } from "./source_pdf_banner";
import { useFilePicker } from "./use_file_picker";

const PDF_ACCEPT = ".pdf,application/pdf";

/**
 * Step 2 of the Add Records wizard — renders the upload form for the picked
 * kind (B0.75 Additional Records, B3 Follow-Up). Above the form, the
 * existing project's RSB renders dimmed for context. Additional Records
 * uploads keep the project's eval_type put; only Follow-Up fires the
 * Comp→Follow-Up promotion, and only on UPLOAD success so `← Change`
 * returns to step 1 without flipping the project.
 */
export const AddRecordsUploadStep: FC<{
  kind: AddRecordsChoice;
  contextPages: number;
  contextLetters: number;
  contextSummary?: string;
  evaluationType?: EvaluationTypeEnum | null;
  // True when the user entered step 2 via step 1; `← Change` returns to
  // step 1. False (direct entry via initialKind) renders a dismiss ✕ instead
  // so there's still an escape hatch.
  canChange: boolean;
  onUpload: (blob: ActiveStorageBlob) => Promise<void>;
  onChange: () => void;
  onDismiss: () => void;
}> = ({
  kind,
  contextPages,
  contextLetters,
  contextSummary,
  evaluationType,
  canChange,
  onUpload,
  onChange,
  onDismiss,
}) => (
  <div>
    {/* Dimmed project-type context per spec — exists only in step 2 */}
    <RecordsStatusBar
      variant="green"
      dimmed
      icon={<RsbCheckIcon />}
      title={`${contextPages.toLocaleString()} pages processed · ${contextLetters.toLocaleString()} letters built`}
      subtitle={
        kind === "additional_records"
          ? "(existing review — additional records will be added)"
          : contextSummary
      }
      badgeStack={
        <EvalTypeBadge
          type={evaluationType ?? EvaluationTypeEnum.Comprehensive}
        />
      }
    />
    <FileManager save={onUpload}>
      {kind === "additional_records" ? (
        <AdditionalRecordsUploadForm
          contextLetters={contextLetters}
          trailing={
            <Step2Trailing
              canChange={canChange}
              onChange={onChange}
              onDismiss={onDismiss}
            />
          }
        />
      ) : (
        <FollowUpUploadForm
          trailing={
            <Step2Trailing
              canChange={canChange}
              onChange={onChange}
              onDismiss={onDismiss}
            />
          }
        />
      )}
      <FileQueue render={renderFileQueueItem} />
    </FileManager>
  </div>
);

// Shared trailing slot for step 2 upload forms. Renders `← Change` when the
// user entered via step 1; otherwise renders a dismiss ✕ (matching the
// step 1 dismiss pattern) since there's nothing to go back to.
const Step2Trailing: FC<{
  canChange: boolean;
  onChange: () => void;
  onDismiss: () => void;
}> = ({ canChange, onChange, onDismiss }) =>
  canChange ? (
    <RsbActionButton onClick={onChange}>← Change</RsbActionButton>
  ) : (
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss add records"
      className="cursor-pointer text-slate-400 hover:text-slate-600"
    >
      <FontAwesomeIcon icon={faXmark} />
    </button>
  );

// D8 fallback for users who prefer browse-to-select over drag-and-drop. Shares
// the hidden-input/picker primitive with the `PriorDrop` above via
// `useFilePicker`, so both paths publish to the same `FileContext` upload queue.
const UploadProcessCta: FC<{ accept: string }> = ({ accept }) => {
  const { inputRef, openPicker, inputProps } = useFilePicker({
    multiple: true,
  });

  return (
    <div className="mt-4 flex justify-end">
      <input {...inputProps} ref={inputRef} accept={accept} />
      <Button type="button" color="indigo" size="sm" onClick={openPicker}>
        Upload &amp; Process
      </Button>
    </div>
  );
};

const AdditionalRecordsUploadForm: FC<{
  contextLetters: number;
  trailing: ReactNode;
}> = ({ contextLetters, trailing }) => (
  <ClassifyOverlayShell
    title="Additional Records"
    subtitle={`Adds new records to the existing review. We'll merge them into the current ${contextLetters.toLocaleString()} letters and flag what's new vs duplicate.`}
    trailing={trailing}
  >
    <PriorDrop
      tone="indigo"
      icon={faArrowUp}
      title="Additional Records"
      subtitle="Drop the new PDF here. Processing starts automatically once the file lands."
      formats="PDF"
      accept={PDF_ACCEPT}
      multiple
    />
    <UploadProcessCta accept={PDF_ACCEPT} />
  </ClassifyOverlayShell>
);

const FollowUpUploadForm: FC<{
  trailing: ReactNode;
}> = ({ trailing }) => (
  <ClassifyOverlayShell
    title="Follow-Up Evaluation"
    subtitle="Compare new records against the existing review and flag what's new."
    trailing={trailing}
  >
    <SourcePdfBanner
      title="Prior records available from the prior comprehensive"
      description="We'll compare new records against the existing review automatically."
    />
    <PriorDrop
      icon={faFileLines}
      title="Drop new records here"
      subtitle="Records received since the prior evaluation"
      formats="PDF"
      accept={PDF_ACCEPT}
      multiple
    />
    <UploadProcessCta accept={PDF_ACCEPT} />
  </ClassifyOverlayShell>
);
